import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdEdit, MdClose, MdSearch, MdInventory, MdWarning, MdArrowUpward, MdArrowDownward, MdRefresh } from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';
import { useSiteSettings, formatMoney } from '../hooks/useSiteSettings';

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

const emptyItem = { item_code: '', name: '', category_id: '', facility_id: '', unit: '', quantity_in_stock: '', minimum_stock: '', unit_cost: '', location_notes: '' };

const TXN_META = {
    in: { bg: '#dcfce7', color: '#15803d', icon: MdArrowUpward },
    out: { bg: '#fee2e2', color: '#b91c1c', icon: MdArrowDownward },
    adjust: { bg: '#e0f2fe', color: '#0369a1', icon: MdRefresh },
};

function TxnBadge({ type }) {
    const { t } = useLanguage();
    const TXN_LABELS = { in: t('inventory.txnStockIn'), out: t('inventory.txnStockOut'), adjust: t('inventory.txnAdjusted') };
    const meta = TXN_META[type] || { bg: '#f1f5f9', color: '#475569', icon: MdRefresh };
    const label = TXN_LABELS[type] || type;
    const Icon = meta.icon;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 12, background: meta.bg, color: meta.color, whiteSpace: 'nowrap' }}>
            <Icon size={12} /> {label}
        </span>
    );
}

export default function Inventory() {
    const { isManager, assignedFacility } = useAuth();
    const { currency } = useSiteSettings();
    const { t } = useLanguage();
    const RWF = n => formatMoney(n, currency);
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [categories, setCategories] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [showItemModal, setShowItemModal] = useState(false);
    const [showTxnModal, setShowTxnModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [form, setForm] = useState(emptyItem);
    const [editId, setEditId] = useState(null);
    const [txnForm, setTxnForm] = useState({ transaction_type: 'out', quantity: '', notes: '', work_order_id: '' });
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('items');

    // Full stock movement log for the Transactions tab — who moved stock, how
    // much, and why. This is an audit trail: it is fetched read-only and the
    // UI deliberately offers no way to edit or delete an entry.
    const [transactions, setTransactions] = useState([]);
    const [txnLogLoading, setTxnLogLoading] = useState(false);
    const [txnLogError, setTxnLogError] = useState('');
    const [txnTypeFilter, setTxnTypeFilter] = useState('');
    const [txnSearch, setTxnSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (search) params.search = search;
            if (filterCat) params.category_id = filterCat;
            if (filterStatus) params.status = filterStatus;

            const [itemsRes, statsRes] = await Promise.allSettled([
                API.get('/inventory', { params }),
                API.get('/inventory/stats')
            ]);

            if (itemsRes.status === 'fulfilled') setItems(itemsRes.value.data);
            else setItems([]);

            if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
            else setStats(null);

            const failed = [itemsRes, statsRes].filter(result => result.status === 'rejected');
            if (failed.length) {
                const failedParts = [];
                if (itemsRes.status === 'rejected') failedParts.push(t('inventory.partItems'));
                if (statsRes.status === 'rejected') failedParts.push(t('inventory.partStatistics'));
                const detail = failed[0].reason?.response?.data?.error;
                setError(`Inventory ${failedParts.join(' and ')} ${t('inventory.loadErrorSuffix')}${detail ? ` ${detail}` : ` ${t('inventory.restartServerHint')}`}`);
            }
        } catch (err) {
            setError(t('inventory.loadErrorGeneric'));
            setItems([]);
            setStats(null);
        } finally { setLoading(false); }
    }, [search, filterCat, filterStatus]);

    const loadTransactions = useCallback(async () => {
        setTxnLogLoading(true);
        setTxnLogError('');
        try {
            const params = {};
            if (txnTypeFilter) params.type = txnTypeFilter;
            if (txnSearch) params.search = txnSearch;
            const res = await API.get('/inventory/transactions', { params });
            setTransactions(res.data);
        } catch (err) {
            setTransactions([]);
            setTxnLogError(err.response?.data?.error || t('inventory.transactionsLoadError'));
        } finally { setTxnLogLoading(false); }
    }, [txnTypeFilter, txnSearch]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        Promise.allSettled([
            API.get('/inventory/categories'),
            API.get('/spaces/buildings')
        ]).then(([categoriesRes, facilitiesRes]) => {
            if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value.data);
            if (facilitiesRes.status === 'fulfilled') setFacilities(facilitiesRes.value.data);
        }).catch(() => {
            setError(t('inventory.filtersLoadError'));
        });
    }, []);
    useEffect(() => { if (activeTab === 'transactions') loadTransactions(); }, [activeTab, loadTransactions]);

    const openCreate = () => {
        const f = { ...emptyItem };
        if (isManager && assignedFacility) f.facility_id = String(assignedFacility.id);
        setForm(f); setEditId(null); setShowItemModal(true);
    };
    const openEdit = item => { setForm({ ...emptyItem, ...item }); setEditId(item.id); setShowItemModal(true); };
    const openTxn = item => { setSelectedItem(item); setTxnForm({ transaction_type: 'out', quantity: '', notes: '', work_order_id: '' }); setShowTxnModal(true); };

    const handleSaveItem = async e => {
        e.preventDefault(); setSaving(true);
        try {
            if (editId) await API.put(`/inventory/${editId}`, form);
            else await API.post('/inventory', form);
            setShowItemModal(false); load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); }
        finally { setSaving(false); }
    };

    const handleTxn = async e => {
        e.preventDefault();
        if (txnForm.transaction_type === 'out' && !txnForm.notes.trim()) {
            alert(translateUiText('Please give a reason for this stock out'));
            return;
        }
        setSaving(true);
        try {
            await API.post(`/inventory/${selectedItem.id}/transaction`, txnForm);
            setShowTxnModal(false); load();
            if (activeTab === 'transactions') loadTransactions();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Transaction failed')); }
        finally { setSaving(false); }
    };

    const STATUS_LABELS = { in_stock: t('inventory.statusInStock'), low_stock: t('inventory.statusLowStock'), out_of_stock: t('inventory.statusOutOfStock') };
    const TAB_LABELS = { items: t('inventory.tabItems'), transactions: t('inventory.tabTransactions') };
    const statusBadge = s => {
        const map = { in_stock: ['#dcfce7', '#15803d'], low_stock: ['#fef9c3', '#b45309'], out_of_stock: ['#fee2e2', '#b91c1c'] };
        const [bg, color] = map[s] || ['#f1f5f9', '#475569'];
        return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: bg, color, whiteSpace: 'nowrap' }}>{STATUS_LABELS[s] || s}</span>;
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>{t('inventory.pageTitle')}</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {isManager && assignedFacility ? `${assignedFacility.name} — ` : ''}{t('inventory.pageSubtitle')}
                    </p>
                </div>
                <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdAdd size={18} /> {t('inventory.addItem')}
                </button>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: 16 }}>
                    {error}
                </div>
            )}

            {/* Stats */}
            {stats && (
                <div className="row g-3 mb-4">
                    <div className="col-md-3 col-6">
                        <div className="stat-card"><div className="stat-icon blue"><MdInventory /></div>
                            <div><div className="stat-value">{stats.total}</div><div className="stat-label">{t('inventory.statTotalItems')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card"><div className="stat-icon yellow"><MdWarning /></div>
                            <div><div className="stat-value">{stats.low_stock}</div><div className="stat-label">{t('inventory.statLowStock')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card"><div className="stat-icon red"><MdWarning /></div>
                            <div><div className="stat-value">{stats.out_of_stock}</div><div className="stat-label">{t('inventory.statOutOfStock')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card"><div className="stat-icon green"><MdInventory /></div>
                            <div><div className="stat-value">{RWF(stats.total_value)}</div><div className="stat-label">{t('inventory.statTotalValue')}</div></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                {['items', 'transactions'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{
                        padding: '9px 20px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 14,
                        color: activeTab === tab ? '#0ea5e9' : '#64748b', textTransform: 'capitalize',
                        borderBottom: activeTab === tab ? '2px solid #0ea5e9' : '2px solid transparent'
                    }}>{TAB_LABELS[tab]}</button>
                ))}
            </div>

            {/* Filters */}
            {activeTab === 'items' && (
                <div className="page-card mb-4">
                    <div style={{ padding: '14px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                            <MdSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input className="form-control" style={{ paddingLeft: 34 }} placeholder={t('inventory.searchItemsPlaceholder')}
                                value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <select className="form-select" style={{ width: 180 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
                            <option value="">All Categories</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select className="form-select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                            <option value="">All Status</option>
                            <option value="in_stock">{t('inventory.statusInStock')}</option>
                            <option value="low_stock">{t('inventory.statusLowStock')}</option>
                            <option value="out_of_stock">{t('inventory.statusOutOfStock')}</option>
                        </select>
                    </div>
                </div>
            )}
            {activeTab === 'transactions' && (
                <div className="page-card mb-4">
                    <div style={{ padding: '14px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                            <MdSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input className="form-control" style={{ paddingLeft: 34 }} placeholder={t('inventory.searchTxnPlaceholder')}
                                value={txnSearch} onChange={e => setTxnSearch(e.target.value)} />
                        </div>
                        <select className="form-select" style={{ width: 160 }} value={txnTypeFilter} onChange={e => setTxnTypeFilter(e.target.value)}>
                            <option value="">{t('inventory.filterAllMovements')}</option>
                            <option value="in">{t('inventory.txnStockIn')}</option>
                            <option value="out">{t('inventory.txnStockOut')}</option>
                            <option value="adjust">{t('inventory.txnAdjusted')}</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Items table */}
            {activeTab === 'items' && (
                <div className="page-card">
                    <div style={{ overflowX: 'auto' }}>
                        {loading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                            <table className="fms-table">
                                <thead>
                                    <tr><th>{t('inventory.colCode')}</th><th>{t('inventory.colName')}</th><th>{t('inventory.colCategory')}</th><th>{t('inventory.colStock')}</th><th>{t('inventory.colMin')}</th><th>{t('inventory.colUnitCost')}</th><th>{t('inventory.colStatus')}</th><th>{t('inventory.colFacility')}</th><th>{t('inventory.colActions')}</th></tr>
                                </thead>
                                <tbody>
                                    {items.map(item => (
                                        <tr key={item.id} style={{ fontWeight: item.status === 'out_of_stock' ? 600 : 'normal' }}>
                                            <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0ea5e9' }}>{item.item_code}</span></td>
                                            <td>
                                                <div style={{ fontWeight: 500, fontSize: 13 }}>{item.name}</div>
                                                {item.location_notes && <div style={{ fontSize: 11, color: '#94a3b8' }}>📍 {item.location_notes}</div>}
                                            </td>
                                            <td style={{ fontSize: 12 }}>{item.category_name || '—'}</td>
                                            <td>
                                                <span style={{ fontWeight: 700, fontSize: 14, color: item.status === 'out_of_stock' ? '#b91c1c' : item.status === 'low_stock' ? '#b45309' : '#15803d' }}>
                                                    {item.quantity_in_stock} {item.unit || ''}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 12, color: '#64748b' }}>{item.minimum_stock} {item.unit || ''}</td>
                                            <td style={{ fontSize: 13 }}>{item.unit_cost > 0 ? RWF(item.unit_cost) : '—'}</td>
                                            <td>{statusBadge(item.status)}</td>
                                            <td style={{ fontSize: 12, color: '#64748b' }}>{item.facility_name || '—'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button className="btn btn-sm" title={t('inventory.txnStockIn')}
                                                        style={{ background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11 }}
                                                        onClick={() => { setSelectedItem(item); setTxnForm({ transaction_type: 'in', quantity: '', notes: '', work_order_id: '' }); setShowTxnModal(true); }}>
                                                        <MdArrowUpward size={13} />
                                                    </button>
                                                    <button className="btn btn-sm" title={t('inventory.txnStockOut')}
                                                        style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11 }}
                                                        onClick={() => openTxn(item)}>
                                                        <MdArrowDownward size={13} />
                                                    </button>
                                                    <button className="btn btn-sm btn-light" onClick={() => openEdit(item)}><MdEdit size={13} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!items.length && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('inventory.noItemsFound')}</td></tr>}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Transactions log — audit trail: read-only, no edit or delete action
               is offered anywhere in this table on purpose. */}
            {activeTab === 'transactions' && (
                <div className="page-card">
                    {txnLogError && (
                        <div className="alert alert-danger" role="alert" style={{ margin: 16 }}>{txnLogError}</div>
                    )}
                    <div style={{ overflowX: 'auto' }}>
                        {txnLogLoading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                            <table className="fms-table">
                                <thead>
                                    <tr><th>{t('inventory.colDate')}</th><th>{t('inventory.colItem')}</th><th>{t('inventory.colMovement')}</th><th>{t('inventory.colQty')}</th><th>{t('inventory.colReason')}</th><th>{t('inventory.colBy')}</th><th>{t('inventory.colWorkOrder')}</th></tr>
                                </thead>
                                <tbody>
                                    {transactions.map(t => (
                                        <tr key={t.id}>
                                            <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                {t.created_at ? new Date(t.created_at).toLocaleString() : '—'}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 500, fontSize: 13 }}>{t.item_name}</div>
                                                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#0ea5e9' }}>{t.item_code}</div>
                                            </td>
                                            <td><TxnBadge type={t.transaction_type} /></td>
                                            <td style={{ fontWeight: 600, fontSize: 13 }}>{t.quantity} {t.unit || ''}</td>
                                            <td style={{ fontSize: 12, maxWidth: 260 }}>{t.notes || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                            <td style={{ fontSize: 12 }}>{t.performed_by_name || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                            <td style={{ fontSize: 12, color: '#64748b' }}>
                                                {t.work_order_id ? (t.wo_number || t.work_order_title || `#${t.work_order_id}`) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                    {!transactions.length && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('inventory.noTransactionsFound')}</td></tr>}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Add/Edit Item Modal */}
            {showItemModal && (
                <Modal title={editId ? t('inventory.modalEditItemTitle') : t('inventory.modalAddItemTitle')} onClose={() => setShowItemModal(false)}>
                    <form onSubmit={handleSaveItem}>
                        <div className="row g-3">
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('inventory.fieldItemCode')}</label>
                                <input className="form-control" value={form.item_code} onChange={e => setForm({ ...form, item_code: e.target.value })} required placeholder={t('inventory.itemCodePlaceholder')} /></div>
                            <div className="col-md-6"><label className="form-label fw-semibold">Name *</label>
                                <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('inventory.fieldCategory')}</label>
                                <select className="form-select" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                                    <option value="">{t('inventory.selectCategory')}</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select></div>
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('inventory.fieldFacility')}</label>
                                {isManager && assignedFacility
                                    ? <input className="form-control" value={assignedFacility.name} readOnly style={{ background: '#f8fafc', color: '#64748b' }} />
                                    : <select className="form-select" value={form.facility_id} onChange={e => setForm({ ...form, facility_id: e.target.value })}>
                                        <option value="">{t('inventory.allFacilities')}</option>
                                        {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                    </select>
                                }</div>
                            <div className="col-md-4"><label className="form-label fw-semibold">{t('inventory.fieldUnit')}</label>
                                <input className="form-control" placeholder={t('inventory.unitPlaceholder')} value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} /></div>
                            <div className="col-md-4"><label className="form-label fw-semibold">{t('inventory.fieldCurrentQty')}</label>
                                <input type="number" className="form-control" step="0.01" value={form.quantity_in_stock} onChange={e => setForm({ ...form, quantity_in_stock: e.target.value })} /></div>
                            <div className="col-md-4"><label className="form-label fw-semibold">{t('inventory.fieldMinimumStock')}</label>
                                <input type="number" className="form-control" step="0.01" value={form.minimum_stock} onChange={e => setForm({ ...form, minimum_stock: e.target.value })} /></div>
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('inventory.fieldUnitCost', '', {currency})}</label>
                                <input type="number" className="form-control" step="0.01" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })} /></div>
                            <div className="col-12"><label className="form-label fw-semibold">{t('inventory.fieldStorageLocation')}</label>
                                <input className="form-control" placeholder={t('inventory.locationPlaceholder')} value={form.location_notes} onChange={e => setForm({ ...form, location_notes: e.target.value })} /></div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowItemModal(false)}>{t('inventory.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('inventory.saving')}</> : t('inventory.saveItem')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Transaction Modal */}
            {showTxnModal && selectedItem && (
                <Modal title={txnForm.transaction_type === 'in' ? t('inventory.modalStockInTitle', '', { name: selectedItem.name }) : t('inventory.modalStockOutTitle', '', { name: selectedItem.name })} onClose={() => setShowTxnModal(false)}>
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                        {t('inventory.currentStockLabel')} <strong style={{ color: '#0ea5e9' }}>{selectedItem.quantity_in_stock} {selectedItem.unit || t('inventory.unitsFallback')}</strong>
                    </div>
                    <form onSubmit={handleTxn}>
                        <div className="row g-3">
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('inventory.fieldTransactionType')}</label>
                                <select className="form-select" value={txnForm.transaction_type} onChange={e => setTxnForm({ ...txnForm, transaction_type: e.target.value })}>
                                    <option value="in">{t('inventory.optionStockInReceived')}</option>
                                    <option value="out">{t('inventory.optionStockOutUsed')}</option>
                                    <option value="adjust">{t('inventory.optionAdjustSetQty')}</option>
                                </select></div>
                            <div className="col-md-6"><label className="form-label fw-semibold">{t('inventory.fieldQuantity')}</label>
                                <input type="number" className="form-control" step="0.01" min={0.01}
                                    max={txnForm.transaction_type === 'out' ? Number(selectedItem.quantity_in_stock) : undefined} required
                                    value={txnForm.quantity} onChange={e => setTxnForm({ ...txnForm, quantity: e.target.value })}
                                    placeholder={txnForm.transaction_type === 'adjust' ? t('inventory.placeholderNewTotalQty') : t('inventory.placeholderQuantity')} /></div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">
                                    {txnForm.transaction_type === 'out' ? t('inventory.fieldReasonStockOut') : t('inventory.fieldNotes')}
                                </label>
                                <textarea className="form-control" rows={2} value={txnForm.notes} required={txnForm.transaction_type === 'out'}
                                    onChange={e => setTxnForm({ ...txnForm, notes: e.target.value })}
                                    placeholder={txnForm.transaction_type === 'out' ? t('inventory.placeholderReasonOut') : t('inventory.placeholderReasonIn')} />
                                {txnForm.transaction_type === 'out' && (
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                        {t('inventory.stockOutAuditNote')}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowTxnModal(false)}>{t('inventory.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('inventory.recording')}</> : t('inventory.recordTransaction')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
